// roadEngine.js — MiST · ROAD SHOOTER (lane-runner).
// Self-contained pseudo-3D canvas engine: parallax forward march, drag-to-steer
// squad, auto-fire bullets, convertible blue/red gates (bullet-driven math),
// destructible +Weapons/+Soldiers barrels, and a trailing troop formation.
// Owns its own RAF loop and input; no dependency on the tap-arena engine.
//
// TWO MODES:
//  • Legacy (default): single endless run to RUN_LENGTH / durationMs. Used by the
//    Game-Demo recorder + PvP arena autopilot. UNCHANGED.
//  • Campaign (opt-in `campaign:true`): 3-stage story flow with named heroes
//    (Jess + GI Bazooka from Stage 1, Nancy rescued mid-Stage 2), 3 lives,
//    converging alien hordes + a Leviathan boss in Stage 3, and freeze-frame
//    interstitial / rescue overlays driven from React via callbacks.

const DPR = Math.max(1, Math.min(3, typeof window !== "undefined" ? window.devicePixelRatio : 1));

const RUN_LENGTH = 1600;   // legacy finish-line distance (world units)
const SPAWN_DIST = 120;    // how far ahead a row spawns
const MARCH_SPEED = 34;    // world units / second
const ROW_GAP = 46;        // world-unit spacing between spawned rows
const MAX_TROOPS = 30;     // regular-wave trailing troop cap
const LANE_SPAN = 0.72;    // max normalised lane offset from centre

// Campaign pacing
const STAGE_LEN = 900;         // world units per stage
const RESCUE_AT = 0.5;         // stage-2 fraction that triggers Nancy rescue
const BOSS_AT = 0.62;          // stage-3 fraction that locks the boss fight
const CAMP_SPEED = 46;

const rnd = (a, b) => a + Math.random() * (b - a);
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

export class RoadEngine {
  constructor(canvas, {
    onUpdate, onFinish, onGameOver, sprites, macro, autopilot, durationMs, speed,
    strafe, spawnRule, facingCamera, bgFull, barrelStyle,
    // campaign extras
    campaign, heroSprites, bossSprite, stageBackdrops, onStageClear, onRescue, onStageEnter, onLifeLost,
    initialRoster, enableRescue, startWeapon, stageLen, bossName, bossHp, rescueSprite, onRescueEncounter,
    // Last Z prologue extras (all opt-in; default OFF → legacy/RoadShooter unchanged)
    crateAt, crateWeaponLevel, dropPodTroops, giBozokAtBoss, healPulse, troopShield,
    onCrate, onDropPod, onGiBozok,
    surfaceSweep, vehicle, alienSprites, sweepBoss,
  } = {}) {
    this.canvas = canvas;
    this.ctx = canvas.getContext("2d");
    this.onUpdate = onUpdate || (() => {});
    this.onFinish = onFinish || (() => {});
    this.onGameOver = onGameOver || (() => {});
    this.images = new Map();
    this.backdrop = null;
    this.bgFull = !!bgFull;
    this.barrelStyle = barrelStyle || "wood";
    this.macro = macro || "forward";
    this.autopilot = !!autopilot;
    this.durationMs = durationMs || 0;
    this.strafe = strafe || null;
    this.spawnRule = spawnRule || "STANDARD";
    this.facingCamera = !!facingCamera;
    this.sprites = sprites || {};
    ["hero", "troop", "villain"].forEach((k) => { if (this.sprites[k]) this._img(this.sprites[k]); });
    this._t0 = 0;

    // ── campaign config ──
    this.campaign = !!campaign;
    this.heroSprites = heroSprites || {};   // { jess, bazooka, nancy } (urls)
    this.bossSprite = bossSprite || null;
    this.stageBackdrops = stageBackdrops || {}; // { 1, 2, 3 }
    this.onStageClear = onStageClear || (() => {});
    this.onRescue = onRescue || (() => {});
    this.onStageEnter = onStageEnter || (() => {});
    this.onLifeLost = onLifeLost || (() => {});
    Object.values(this.heroSprites).forEach((u) => this._img(u));
    if (this.bossSprite) this._img(this.bossSprite);

    this.speed = speed || (this.campaign ? CAMP_SPEED : MARCH_SPEED);
    this._baseSpeed = this.speed;
    this.troops = this.campaign ? 0 : 5;
    this.weaponLevel = startWeapon || 1;
    this.stageLen = stageLen || STAGE_LEN;
    this.distance = 0;
    this.nextRowAt = 40;
    this.entities = [];   // gates + barrels
    this.bullets = [];
    this.particles = [];
    this.laneNorm = 0;
    this.targetLane = 0;
    this.fireAcc = 0;
    this.over = false;
    this.w = 0; this.h = 0; this.raf = null; this.last = 0;

    // campaign runtime state
    this.stage = 1;
    this.stageDist = 0;
    this.lives = 3;
    this.paused = false;
    this.rescued = enableRescue === false;   // pre-satisfied → rescue never triggers
    this.roster = initialRoster ? [...initialRoster] : ["jess", "bazooka"];
    this.bossName = bossName || "ALIEN LEVIATHAN";
    this.bossHp = bossHp || 70;
    this.rescueSprite = rescueSprite || null;
    if (this.rescueSprite) this._img(this.rescueSprite);
    this.onRescueEncounter = onRescueEncounter || (() => {});
    this.rescueEncounter = false;   // stage-2 medbay swarm fight
    this.rescueToKill = 7; this.rescueKilled = 0; this.rescueSpawned = 0;
    this.atkBuff = 1;               // tri-faction synergy multiplier
    this.synergy = false;
    this.aliens = [];
    this.alienAcc = 0;
    this.boss = null;
    this.nancyDrop = null;   // { y, targetY } descent animation
    this.hitFlash = 0;

    // Last Z prologue runtime flags (opt-in)
    this.crateAt = crateAt || null;
    this.crateWeaponLevel = crateWeaponLevel || 3;
    this.dropPodTroops = dropPodTroops || 0;
    this.giBozokAtBoss = !!giBozokAtBoss;
    this.healPulse = !!healPulse;
    this.troopShield = !!troopShield;
    this.onCrate = onCrate || (() => {});
    this.onDropPod = onDropPod || (() => {});
    this.onGiBozok = onGiBozok || (() => {});
    this._crateDone = false;
    this._dropPodDone = false;
    this._healAcc = 0;
    // Phase B surface sweep: single wide-lane stage vs a monster swarm; win on
    // reaching stageLen (no interstitial, no boss).
    this.surfaceSweep = !!surfaceSweep;
    // Combat vehicle (opt-in): armour buffer + auto-firing turret + ram-through barricades.
    this.vehicle = vehicle || null;
    this.armorMax = vehicle ? Math.max(0, vehicle.armor || 0) : 0;
    this.armor = this.armorMax;
    this._turretAcc = 0;
    this.alienSprites = alienSprites || [];
    this.sweepBoss = !!sweepBoss;
    this._sweepBossDone = false;

    this._down = this._down.bind(this);
    this._move = this._move.bind(this);
    this._up = this._up.bind(this);
    this._loop = this._loop.bind(this);
  }

  mount() {
    this.resize();
    this.canvas.addEventListener("pointerdown", this._down);
    this.canvas.addEventListener("pointermove", this._move);
    window.addEventListener("pointerup", this._up);
    this.last = performance.now();
    this._t0 = this.last;
    if (this.campaign) this._enterStage(1);
    this._emit();
    this.raf = requestAnimationFrame(this._loop);
  }
  unmount() {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.canvas.removeEventListener("pointerdown", this._down);
    this.canvas.removeEventListener("pointermove", this._move);
    window.removeEventListener("pointerup", this._up);
  }
  resize() {
    const r = this.canvas.getBoundingClientRect();
    this.w = r.width; this.h = r.height;
    this.canvas.width = Math.round(r.width * DPR);
    this.canvas.height = Math.round(r.height * DPR);
    this.ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
  }
  setBackdrop(url) { this.backdrop = url || null; if (url) this._img(url); }
  _img(url) {
    if (!url) return null;
    let im = this.images.get(url);
    if (!im) { im = new Image(); im.__r = false; im.onload = () => { im.__r = true; }; im.src = url; this.images.set(url, im); }
    return im.__r ? im : null;
  }
  _emit() {
    if (this.campaign) {
      this.onUpdate({
        troops: this.troops, weaponLevel: this.weaponLevel,
        weaponTier: Math.min(3, this.weaponLevel), lives: this.lives, stage: this.stage,
        progress: clamp(this.stageDist / this.stageLen, 0, 1), boss: this.boss ? { hp: this.boss.hp, max: this.boss.max } : null,
        synergy: this.synergy, rescueEncounter: this.rescueEncounter,
        armor: this.armor, armorMax: this.armorMax, turret: this.vehicle ? this.vehicle.turret : "none",
      });
    } else {
      this.onUpdate({ troops: this.troops, weaponLevel: this.weaponLevel, progress: clamp(this.distance / RUN_LENGTH, 0, 1) });
    }
  }

  // ── Campaign control (called from React) ──────────────────────────────────
  pause() { this.paused = true; }
  resume() { this.paused = false; this.last = performance.now(); }
  _enterStage(stage) {
    this.stage = stage;
    this.stageDist = 0;
    this.entities = []; this.bullets = []; this.aliens = []; this.boss = null;
    this.nextRowAt = 40; this.distance = 0;
    this.spawnRule = stage === 1 ? "STANDARD" : stage === 2 ? "CORRIDOR" : "BLITZ";
    this.barrelStyle = stage === 2 ? "wood" : "steel";
    const bg = this.stageBackdrops[stage];
    if (bg) { this.bgFull = true; this.setBackdrop(bg); }
    // Part 3 — Tactical Drop: reinforcements attach on entering stage 2.
    if (stage === 2 && this.dropPodTroops > 0 && !this._dropPodDone) {
      this._dropPodDone = true;
      this.troops = Math.min(MAX_TROOPS, this.troops + this.dropPodTroops);
      this.onDropPod(this.dropPodTroops);
    }
    this.onStageEnter({ stage });
    this._emit();
  }
  // Advance from an interstitial (React taps "advance").
  advanceStage() {
    if (this.stage >= 3) return;
    this._enterStage(this.stage + 1);
    this.resume();
  }
  // Complete the Nancy rescue (React dismisses the breach overlay).
  completeRescue() {
    if (this.rescued) { this.resume(); return; }
    this.rescued = true;
    this.nancyDrop = { y: -40, targetY: this._playerY };
    // Tri-faction synergy: +15% ATK / +15% DEF the instant Nancy joins.
    this.atkBuff = 1.15;
    this.synergy = true;
    this.speed = this._baseSpeed; // resume the march
    this._emit();
    this.resume();
  }
  // Stage-2 medbay rescue encounter: freeze the march, clear the lane, spawn a
  // localized alien swarm the fireteam must wipe to breach the boarded medbay.
  _startRescueEncounter() {
    this.rescueEncounter = true;
    this.speed = 0;
    this.entities = []; this.aliens = [];
    this.rescueToKill = 7; this.rescueKilled = 0; this.rescueSpawned = 0; this.alienAcc = 0;
    this.onRescueEncounter();
    this._emit();
  }

  // ── Input: drag anywhere to steer the squad across lanes ──────────────────
  _setLaneFromX(clientX) {
    const rect = this.canvas.getBoundingClientRect();
    const px = clientX - rect.left;
    this.targetLane = clamp(((px - this.w / 2) / (this.w * 0.42)) * LANE_SPAN, -LANE_SPAN, LANE_SPAN);
  }
  _down(e) { this.dragging = true; this._setLaneFromX(e.clientX); }
  _move(e) { if (this.dragging) this._setLaneFromX(e.clientX); }
  _up() { this.dragging = false; }

  // ── Perspective helpers ───────────────────────────────────────────────────
  get _horizonY() { return this.h * 0.20; }
  get _playerY() { return this.h * 0.82; }
  _progressOf(dz) { return clamp(1 - dz / SPAWN_DIST, 0, 1); }
  _screen(dz, laneNorm) {
    const p = this._progressOf(dz);
    const y = this._horizonY + (this._playerY - this._horizonY) * p;
    const half = 6 + (this.w * 0.44 - 6) * p;
    const x = this.w / 2 + laneNorm * half;
    const scale = 0.18 + 0.82 * p;
    return { x, y, scale, p };
  }

  // ── Spawning ──────────────────────────────────────────────────────────────
  _spawnRow() {
    const r = this.spawnRule || "STANDARD";
    const gateFreq = { BLITZ: 0.85, CORRIDOR: 0.85, LOOT: 0.2, BOSS: 0.5, STANDARD: 0.55 }[r] ?? 0.55;
    const mkGate = (laneNorm, blue) => {
      if (blue) { const mult = Math.random() < 0.35; this.entities.push({ type: "gate", dz: SPAWN_DIST, laneNorm, op: mult ? "x" : "+", value: mult ? 2 : Math.round(rnd(3, 9)) }); }
      else this.entities.push({ type: "gate", dz: SPAWN_DIST, laneNorm, op: "-", value: -Math.round(rnd(4, 12)) });
    };
    if (Math.random() < gateFreq) {
      let leftBlue, rightBlue;
      if (r === "BLITZ") { leftBlue = true; rightBlue = true; }
      else if (r === "CORRIDOR") { leftBlue = Math.random() < 0.4; rightBlue = !leftBlue; }
      else { leftBlue = Math.random() < 0.5; rightBlue = !leftBlue; }
      mkGate(-0.4, leftBlue); mkGate(0.4, rightBlue);
    } else {
      if (r === "BLITZ") { this.entities.push({ type: "gate", dz: SPAWN_DIST, laneNorm: 0, op: "x", value: 2 }); return; }
      const lanes = Math.random() < 0.5 ? [0] : [-0.42, 0.42];
      const weaponBias = r === "LOOT" ? 0.85 : 0.4;
      for (const ln of lanes) {
        const weapons = Math.random() < weaponBias;
        this.entities.push({
          type: "barrel", dz: SPAWN_DIST, laneNorm: ln, hp: 6,
          reward: weapons ? { kind: "weapons", amount: 1 } : { kind: "soldiers", amount: Math.round(rnd(3, 7)) },
        });
      }
    }
  }

  _fire() {
    const { x, y } = this._screen(0, this.laneNorm);
    const spread = this.weaponLevel > 2 ? [-10, 0, 10] : this.weaponLevel > 1 ? [-6, 6] : [0];
    const dir = this.facingCamera ? 1 : -1;
    for (const dx of spread) this.bullets.push({ x: x + dx, y: y + dir * 24, vy: dir * 640 });
  }

  _burst(x, y, color, n = 12) {
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2, s = rnd(40, 150);
      this.particles.push({ x, y, vx: Math.cos(a) * s, vy: Math.sin(a) * s, life: 1, color });
    }
  }

  _applyGate(g) {
    if (g.value > 0) {
      if (g.op === "x") this.troops = Math.min(MAX_TROOPS, this.troops * g.value);
      else this.troops = Math.min(MAX_TROOPS, this.troops + g.value);
    } else if (g.value < 0) {
      this.troops = this.troops + g.value;
    }
    this.troops = Math.round(this.troops);
    this._emit();
    if (!this.campaign && this.troops <= 0) { this.troops = 0; this._end(false); }
    if (this.campaign && this.troops < 0) this.troops = 0;
  }

  _end(win) {
    if (this.over) return;
    this.over = true;
    win ? this.onFinish({ troops: this.troops, weaponLevel: this.weaponLevel }) : this.onGameOver({ troops: this.troops });
  }

  _loseLife() {
    if (this.lives <= 0) return;
    // Vehicle armour is the outermost buffer — soaks hits before troops/hearts.
    if (this.vehicle && this.armor > 0) { this.armor -= 1; this.hitFlash = 1; this._emit(); return; }
    // Temp troops act as a layered shield buffer — decrement them BEFORE a heart.
    if (this.troopShield && this.troops > 0) { this.troops -= 1; this.hitFlash = 1; this._emit(); return; }
    this.lives -= 1;
    this.hitFlash = 1;
    this.onLifeLost(this.lives);
    this._emit();
    if (this.lives <= 0) this._end(false);
  }

  // ── Shared per-frame steps (used by both modes) ───────────────────────────
  _stepSteer(dt) {
    if (this.autopilot) this._autoSteer();
    this.laneNorm += (this.targetLane - this.laneNorm) * Math.min(1, dt / 90);
  }
  _stepFire(dt) {
    this.fireAcc += dt;
    const interval = Math.max(110, (300 - this.weaponLevel * 30) / this.atkBuff);
    while (this.fireAcc >= interval) { this.fireAcc -= interval; this._fire(); }
    if (this.vehicle && this.vehicle.turret && this.vehicle.turret !== "none") this._stepTurret(dt);
  }
  // Mounted turret auto-fire (parallel to squad fire). MG = fast light rounds;
  // Missile Pod = slow heavy rounds (×4 damage).
  _stepTurret(dt) {
    this._turretAcc += dt;
    const missile = this.vehicle.turret === "missile";
    const cfg = missile ? { interval: 850, dmg: 4, color: "#ff7a3c" } : { interval: 160, dmg: 1, color: "#ffe08a" };
    const dir = this.facingCamera ? 1 : -1;
    while (this._turretAcc >= cfg.interval) {
      this._turretAcc -= cfg.interval;
      const { x, y } = this._screen(0, this.laneNorm);
      this.bullets.push({ x, y: y + dir * 16, vy: dir * 760, dmg: cfg.dmg, turret: true, color: cfg.color });
    }
  }
  _stepBullets(sec) {
    for (const b of this.bullets) b.y += b.vy * sec;
    this.bullets = this.bullets.filter((b) => this.facingCamera ? b.y < this.h + 24 : b.y > this._horizonY - 60);
  }
  _stepEntities(sec) {
    for (const en of this.entities) {
      en.dz -= this.speed * sec;
      if (en.dz <= 0 && !en.resolved) {
        en.resolved = true;
        const near = Math.abs(en.laneNorm - this.laneNorm) < 0.28;
        if (en.type === "gate" && near) this._applyGate(en);
        else if (en.type === "barrel") {
          if (this.facingCamera) {
            const s = this._screen(0, en.laneNorm);
            this._burst(s.x, s.y, en.reward.kind === "weapons" ? "#f5c542" : "#37e0a0");
            if (en.reward.kind === "weapons") this.weaponLevel = Math.min(6, this.weaponLevel + en.reward.amount);
            else this.troops = Math.min(MAX_TROOPS, this.troops + en.reward.amount);
            this._emit();
          } else if (this.campaign && near) {
            // Un-destroyed barrel crashes into the squad → costs a life.
            const s = this._screen(0, en.laneNorm);
            this._burst(s.x, s.y, "#ff5570", 16);
            this._loseLife();
          }
        } else if (en.type === "barricade") {
          const s = this._screen(0, en.laneNorm);
          if (near) {
            // Ram-through: costs vehicle armour (or a life if unarmoured) but clears the path.
            this._burst(s.x, s.y, "#c99a4a", 20);
            if (this.vehicle && this.armor > 0) { this.armor = Math.max(0, this.armor - 3); this.hitFlash = 1; this._emit(); }
            else this._loseLife();
          }
        }
      }
    }
    this.entities = this.entities.filter((e) => e.dz > -8);
  }
  _collideBullets() {
    for (const b of this.bullets) {
      if (b.dead) continue;
      // campaign: bullets vs aliens + boss first
      if (this.campaign) {
        if (this._bulletVsAliens(b)) continue;
        if (this._bulletVsBoss(b)) continue;
      }
      for (const en of this.entities) {
        if (en.resolved || en.dz <= 0) continue;
        const s = this._screen(en.dz, en.laneNorm);
        const halfW = (en.type === "gate" ? 46 : 26) * s.scale;
        const halfH = (en.type === "gate" ? 30 : 26) * s.scale;
        if (Math.abs(b.x - s.x) < halfW && Math.abs(b.y - s.y) < halfH) {
          b.dead = true;
          if (en.type === "gate") {
            if (en.value < 0) { en.value += 1; if (en.value >= 0) { en.op = "+"; en.value = 1; } }
            else en.value += 1;
          } else if (en.type === "barricade") {
            en.hp -= (b.dmg || 1);
            if (en.hp <= 0) { en.resolved = true; this._burst(s.x, s.y, "#c99a4a", 16); }
          } else {
            en.hp -= 1;
            if (en.hp <= 0) {
              en.resolved = true;
              this._burst(s.x, s.y, en.reward.kind === "weapons" ? "#f5c542" : "#37e0a0");
              if (en.reward.kind === "weapons") this.weaponLevel = Math.min(6, this.weaponLevel + en.reward.amount);
              else this.troops = Math.min(MAX_TROOPS, this.troops + en.reward.amount);
              this._emit();
            }
          }
          break;
        }
      }
    }
    this.bullets = this.bullets.filter((b) => !b.dead);
  }
  _stepParticles(sec) {
    for (const p of this.particles) { p.x += p.vx * sec; p.y += p.vy * sec; p.life -= sec * 1.6; }
    this.particles = this.particles.filter((p) => p.life > 0);
  }

  // ── Campaign: aliens + boss ───────────────────────────────────────────────
  _spawnAlien() {
    const sp = this.alienSprites;
    if (sp && sp.length) {
      // Surface variety: Sprawler (big/tanky/slow) vs Acid Hatchling (small/fast).
      const big = Math.random() < 0.4;
      this.aliens.push({
        x: rnd(this.w * 0.12, this.w * 0.88), y: -20,
        speed: rnd(70, 110) * (big ? 0.82 : 1.2), hp: big ? 5 : 2, elite: big,
        r: big ? 22 : 13, sprite: big ? sp[0] : (sp[1] || sp[0]),
      });
      return;
    }
    const elite = Math.random() < 0.22;
    this.aliens.push({
      x: rnd(this.w * 0.12, this.w * 0.88), y: -20,
      speed: rnd(70, 110) * (elite ? 0.9 : 1), hp: elite ? 4 : 2, elite, r: elite ? 20 : 14,
    });
  }
  _updateAliens(sec) {
    const base = this._screen(0, this.laneNorm);
    for (const a of this.aliens) {
      const dx = base.x - a.x, dy = base.y - a.y;
      const ang = Math.atan2(dy, dx);
      a.x += Math.cos(ang) * a.speed * sec;
      a.y += Math.sin(ang) * a.speed * sec;
      if (Math.hypot(base.x - a.x, base.y - a.y) < a.r + 16) { a.dead = true; this._burst(a.x, a.y, "#c04bff", 14); this._loseLife(); if (this.rescueEncounter) this.rescueKilled++; }
    }
    this.aliens = this.aliens.filter((a) => !a.dead && a.y < this.h + 40);
  }
  _bulletVsAliens(b) {
    for (const a of this.aliens) {
      if (a.dead) continue;
      if (Math.hypot(b.x - a.x, b.y - a.y) < a.r) {
        b.dead = true; a.hp -= (b.dmg || 1);
        if (a.hp <= 0) { a.dead = true; this._burst(a.x, a.y, a.elite ? "#7dff5a" : "#c04bff", 12); if (this.rescueEncounter) this.rescueKilled++; }
        return true;
      }
    }
    return false;
  }
  _spawnBoss() {
    this.aliens = [];      // clear the horde…
    this.entities = [];    // …and the gates/barrels so bullets reach the boss
    // Part 5 — Gi Bozok drops into the arena to complete the trio.
    if (this.giBozokAtBoss && !this.roster.includes("bazooka")) { this.roster.push("bazooka"); this.onGiBozok(); }
    this.boss = { hp: this.bossHp, max: this.bossHp, x: this.w / 2, y: this.h * 0.16, bob: 0 };
    this.speed = 0; // lock scroll for the showdown
    this._emit();
  }
  _bulletVsBoss(b) {
    if (!this.boss) return false;
    const bw = this.w * 0.32, bh = this.h * 0.16;
    if (Math.abs(b.x - this.boss.x) < bw / 2 && Math.abs(b.y - this.boss.y) < bh / 2) {
      b.dead = true; this.boss.hp -= (b.dmg || 1); this._burst(b.x, b.y, "#7dff5a", 4);
      this.boss.flash = 1;
      if (this.boss.hp <= 0) { this._burst(this.boss.x, this.boss.y, "#ffd24a", 40); this.boss = null; this._end(true); }
      else this._emit();
      return true;
    }
    return false;
  }

  // ── Update dispatch ───────────────────────────────────────────────────────
  _update(dt) {
    if (this.over) return;
    if (this.campaign) { if (this.paused) return; this._updateCampaign(dt); return; }
    const sec = dt / 1000;
    this.distance += this.speed * sec;
    if (this.distance >= RUN_LENGTH) { this._end(true); return; }
    if (this.durationMs && this._t0 && (performance.now() - this._t0) >= this.durationMs) { this._end(true); return; }
    this._stepSteer(dt);
    if (this.distance >= this.nextRowAt) { this._spawnRow(); this.nextRowAt += ROW_GAP; }
    this._stepFire(dt);
    this._stepEntities(sec);
    this._stepBullets(sec);
    this._collideBullets();
    this._stepParticles(sec);
  }

  _updateCampaign(dt) {
    const sec = dt / 1000;
    if (this.hitFlash > 0) this.hitFlash = Math.max(0, this.hitFlash - sec * 2.2);
    if (this.boss && this.boss.flash > 0) this.boss.flash = Math.max(0, this.boss.flash - sec * 4);

    // Nancy drop-in animation (does not gate the sim).
    if (this.nancyDrop) {
      this.nancyDrop.y += (this.nancyDrop.targetY - this.nancyDrop.y) * Math.min(1, dt / 220);
      if (Math.abs(this.nancyDrop.y - this.nancyDrop.targetY) < 6) {
        if (!this.roster.includes("nancy")) this.roster.push("nancy");
        this.nancyDrop = null;
      }
    }

    // Boss showdown locks distance; otherwise march the stage forward.
    if (!this.boss) this.stageDist += this.speed * sec;

    // Part 2 — Firepower crate: mid-stage-1 weapon upgrade (assault rifle spray).
    if (this.stage === 1 && this.crateAt && !this._crateDone && this.stageDist >= this.stageLen * this.crateAt) {
      this._crateDone = true;
      this.weaponLevel = this.crateWeaponLevel;
      const s = this._screen(0, this.laneNorm);
      this._burst(s.x, s.y, "#f5c542", 28);
      this.onCrate(this.weaponLevel);
      this._emit();
    }
    // Part 4 — Nancy's heal_pulse(): passive troop regen once she has joined.
    if (this.healPulse && this.rescued && !this.boss && !this.rescueEncounter) {
      this._healAcc += dt;
      if (this._healAcc >= 3500) { this._healAcc -= 3500; if (this.troops < 6) { this.troops = Math.min(6, this.troops + 1); this._emit(); } }
    }
    // Surface sweep — periodic abandoned barricades to ram through / shoot down.
    if (this.surfaceSweep) {
      this._barrAcc = (this._barrAcc || 0) + dt;
      if (this._barrAcc >= 2600) {
        this._barrAcc -= 2600;
        if (this.entities.filter((e) => e.type === "barricade").length < 2) {
          const lane = [-0.42, 0, 0.42][Math.floor(Math.random() * 3)];
          this.entities.push({ type: "barricade", dz: SPAWN_DIST, laneNorm: lane, hp: 6 });
        }
      }
    }

    // Stage-2 rescue: start the medbay swarm encounter at the midpoint. Once the
    // swarm is wiped, freeze and hand the breach overlay to React.
    if (this.stage === 2 && !this.rescued && !this.rescueEncounter && this.stageDist >= this.stageLen * RESCUE_AT) {
      this._startRescueEncounter();
    }
    if (this.rescueEncounter && this.rescueKilled >= this.rescueToKill && this.aliens.length === 0) {
      this.rescueEncounter = false; this.pause(); this.onRescue(); return;
    }
    // Stage-3 boss trigger.
    if (this.stage === 3 && !this.boss && this.stageDist >= this.stageLen * BOSS_AT) this._spawnBoss();
    // Surface/corridor boss: spawn at the run's end instead of a clean clear.
    if (this.surfaceSweep && this.sweepBoss && !this.boss && !this._sweepBossDone && this.stageDist >= this.stageLen) { this._sweepBossDone = true; this._spawnBoss(); }
    // Stage clear (stages 1 & 2 end on distance; stage 3 / sweep-boss end on boss death).
    if (this.stage < 3 && !this.rescueEncounter && this.stageDist >= this.stageLen && !(this.surfaceSweep && this.sweepBoss)) {
      this.pause(); this.entities = []; this.bullets = []; this.aliens = [];
      if (this.surfaceSweep) { this._end(true); return; }
      this.onStageClear(this.stage); return;
    }

    this._stepSteer(dt);
    if (!this.boss && this.stageDist >= this.nextRowAt) { this._spawnRow(); this.nextRowAt += ROW_GAP; }
    this._stepFire(dt);
    this._stepEntities(sec);

    // Stage-3 alien hordes converge on the squad (capped so they don't wall off the boss).
    if (this.stage === 3 || this.surfaceSweep) {
      this.alienAcc += dt;
      const rate = this.boss ? 2400 : 1400;
      const cap = this.boss ? 2 : 4;
      while (this.alienAcc >= rate) { this.alienAcc -= rate; if (this.aliens.length < cap) this._spawnAlien(); }
      this._updateAliens(sec);
    } else if (this.rescueEncounter) {
      // Localized medbay swarm — spawn up to rescueToKill, keep ≤3 on screen.
      this.alienAcc += dt;
      while (this.alienAcc >= 700) {
        this.alienAcc -= 700;
        if (this.aliens.length < 3 && this.rescueSpawned < this.rescueToKill) { this._spawnAlien(); this.rescueSpawned++; }
      }
      this._updateAliens(sec);
    }

    this._stepBullets(sec);
    this._collideBullets();
    this._stepParticles(sec);
  }

  // ── Render ──────────────────────────────────────────────────────────────
  _render() {
    const ctx = this.ctx, w = this.w, h = this.h;
    ctx.clearRect(0, 0, w, h);
    const bg = this._img(this.backdrop);
    if (bg) {
      const iw = bg.naturalWidth || 1, ih = bg.naturalHeight || 1;
      const s = Math.max(w / iw, h / ih);
      const dw = iw * s, dh = ih * s;
      const scroll = this.campaign ? this.stageDist : this.distance;
      const off = (scroll * 2.2) % dh;
      const eff = this.facingCamera ? (dh - off) : off;
      ctx.globalAlpha = this.bgFull ? 1 : 0.5;
      ctx.drawImage(bg, w / 2 - dw / 2, -dh + eff, dw, dh);
      ctx.drawImage(bg, w / 2 - dw / 2, eff, dw, dh);
      ctx.globalAlpha = 1;
    } else {
      const g = ctx.createLinearGradient(0, 0, 0, h);
      g.addColorStop(0, "#0a0f22"); g.addColorStop(1, "#05070f");
      ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
    }

    if (!(bg && this.bgFull)) {
      const hy = this._horizonY, py = this._playerY;
      const nearHalf = w * 0.44, farHalf = 6;
      ctx.fillStyle = "rgba(10,16,30,0.82)";
      ctx.beginPath();
      ctx.moveTo(w / 2 - farHalf, hy); ctx.lineTo(w / 2 + farHalf, hy);
      ctx.lineTo(w / 2 + nearHalf, h); ctx.lineTo(w / 2 - nearHalf, h);
      ctx.closePath(); ctx.fill();
      ctx.strokeStyle = "rgba(0,213,255,0.55)"; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(w / 2 - farHalf, hy); ctx.lineTo(w / 2 - nearHalf, h);
      ctx.moveTo(w / 2 + farHalf, hy); ctx.lineTo(w / 2 + nearHalf, h); ctx.stroke();
      ctx.strokeStyle = "rgba(0,213,255,0.16)"; ctx.lineWidth = 1;
      const dd = this.campaign ? this.stageDist : this.distance;
      for (let i = 0; i < 14; i++) {
        const p = ((i / 14) + ((dd * 0.02) % (1 / 14))) % 1;
        const y = hy + (py + (h - py) - hy) * p * p;
        const half = farHalf + (nearHalf - farHalf) * p * p;
        ctx.beginPath(); ctx.moveTo(w / 2 - half, y); ctx.lineTo(w / 2 + half, y); ctx.stroke();
      }
    }

    const sorted = [...this.entities].sort((a, b) => b.dz - a.dz);
    for (const en of sorted) {
      if (en.dz <= 0 || en.dz > SPAWN_DIST) continue;
      const s = this._screen(en.dz, en.laneNorm);
      if (en.type === "gate") this._drawGate(ctx, en, s);
      else if (en.type === "barricade") this._drawBarricade(ctx, en, s);
      else this._drawBarrel(ctx, en, s);
    }

    // campaign boss + aliens + rescue medbay
    if (this.campaign) {
      if (this.rescueEncounter) this._drawMedbay(ctx);
      if (this.boss) this._drawBoss(ctx);
      for (const a of this.aliens) this._drawAlien(ctx, a);
    }

    ctx.fillStyle = "#8ff6ff";
    for (const b of this.bullets) { ctx.fillStyle = b.color || "#8ff6ff"; ctx.beginPath(); ctx.ellipse(b.x, b.y, b.turret ? (b.dmg > 1 ? 4 : 3) : 2.5, b.turret ? 9 : 7, 0, 0, 7); ctx.fill(); }

    if (this.campaign) this._drawCampaignSquad(ctx);
    else this._drawSquad(ctx);

    if (this.spawnRule === "BOSS" && !this.campaign) {
      const bx = w / 2, by = h * 0.13, bw = w * 0.5, bh = h * 0.2;
      const villain = this._img(this.sprites.villain);
      if (villain) ctx.drawImage(villain, bx - bw / 2, by - bh / 2, bw, bh);
      else {
        ctx.fillStyle = "rgba(180,40,60,0.92)"; ctx.strokeStyle = "#ff5570"; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.roundRect(bx - bw / 2, by - bh / 2, bw, bh, 12); ctx.fill(); ctx.stroke();
      }
    }

    for (const p of this.particles) {
      ctx.globalAlpha = Math.max(0, p.life); ctx.fillStyle = p.color;
      ctx.beginPath(); ctx.arc(p.x, p.y, 2.5, 0, 7); ctx.fill();
    }
    ctx.globalAlpha = 1;

    if (this.campaign && this.hitFlash > 0) {
      ctx.fillStyle = `rgba(255,40,60,${(this.hitFlash * 0.32).toFixed(3)})`;
      ctx.fillRect(0, 0, w, h);
    }
  }

  _groundShadow(ctx, x, yBase, halfW, scale) {
    ctx.save();
    ctx.globalAlpha = Math.min(0.5, 0.16 + scale * 0.4);
    ctx.fillStyle = "#000";
    ctx.beginPath(); ctx.ellipse(x, yBase, halfW, Math.max(2, halfW * 0.26), 0, 0, 7); ctx.fill();
    ctx.restore();
  }

  _drawGate(ctx, g, s) {
    const pos = g.value >= 0;
    const flash = !pos && (Math.floor(performance.now() / 180) % 2 === 0);
    const col = pos ? "#25e06a" : (flash ? "#ff3b4e" : "#d62a44");
    const wgt = 42 * s.scale, hgt = 30 * s.scale;
    const x0 = s.x - wgt, y0 = s.y - hgt, ww = wgt * 2, hh = hgt * 2;
    const rad = 6 * s.scale;
    this._groundShadow(ctx, s.x, y0 + hh + 3 * s.scale, wgt * 0.9, s.scale);
    ctx.fillStyle = pos ? "rgba(37,224,106,0.16)" : "rgba(255,59,78,0.16)";
    ctx.beginPath(); ctx.roundRect(x0, y0, ww, hh, rad); ctx.fill();
    ctx.save();
    ctx.beginPath(); ctx.roundRect(x0, y0, ww, hh, rad); ctx.clip();
    const t = (Math.sin(performance.now() / 550 + s.x * 0.01) * 0.5 + 0.5) * 0.7;
    const pg = ctx.createLinearGradient(x0, y0, x0 + ww, y0 + hh);
    const cc = pos ? "rgba(37,224,106,0.32)" : "rgba(255,59,78,0.32)";
    pg.addColorStop(0, cc);
    pg.addColorStop(Math.min(0.98, t + 0.15), "rgba(255,255,255,0.16)");
    pg.addColorStop(1, cc);
    ctx.fillStyle = pg; ctx.fillRect(x0, y0, ww, hh);
    ctx.restore();
    ctx.strokeStyle = col; ctx.lineWidth = 3 * s.scale;
    ctx.shadowBlur = 12 * s.scale; ctx.shadowColor = col;
    ctx.beginPath(); ctx.roundRect(x0, y0, ww, hh, rad); ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.strokeStyle = "rgba(255,255,255,0.5)"; ctx.lineWidth = Math.max(1, 1 * s.scale);
    ctx.beginPath(); ctx.roundRect(x0 + 3 * s.scale, y0 + 3 * s.scale, ww - 6 * s.scale, hh - 6 * s.scale, Math.max(2, rad - 2)); ctx.stroke();
    ctx.fillStyle = col; const ps = 3.5 * s.scale;
    [[x0, y0], [x0 + ww, y0], [x0, y0 + hh], [x0 + ww, y0 + hh]].forEach(([cx, cy]) => { ctx.beginPath(); ctx.arc(cx, cy, ps, 0, 7); ctx.fill(); });
    ctx.fillStyle = "#ffffff"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.font = `900 ${Math.round(22 * s.scale)}px 'Barlow Condensed', sans-serif`;
    const label = g.op === "x" ? `×${g.value}` : (g.value >= 0 ? `+${g.value}` : `${g.value}`);
    ctx.fillText(label, s.x, s.y);
  }

  _drawBarrel(ctx, b, s) {
    const r = 22 * s.scale;
    if (this.barrelStyle === "steel") {
      const bw = r * 1.42, bh = r * 2, x0 = s.x - bw / 2, y0 = s.y - bh / 2;
      this._groundShadow(ctx, s.x, y0 + bh + 2 * s.scale, bw * 0.6, s.scale);
      const grad = ctx.createLinearGradient(x0, 0, x0 + bw, 0);
      grad.addColorStop(0, "#1c2a38"); grad.addColorStop(0.5, "#8aa6c0"); grad.addColorStop(1, "#1c2a38");
      ctx.fillStyle = grad; ctx.beginPath(); ctx.roundRect(x0, y0, bw, bh, 4 * s.scale); ctx.fill();
      [0.16, 0.84].forEach((f) => {
        const yy = y0 + bh * f;
        ctx.strokeStyle = "rgba(0,0,0,0.4)"; ctx.lineWidth = Math.max(1.5, 2 * s.scale);
        ctx.beginPath(); ctx.moveTo(x0, yy); ctx.lineTo(x0 + bw, yy); ctx.stroke();
        ctx.strokeStyle = "rgba(0,229,255,0.85)"; ctx.lineWidth = Math.max(1, 1.2 * s.scale);
        ctx.shadowBlur = 6 * s.scale; ctx.shadowColor = "#00e5ff";
        ctx.beginPath(); ctx.moveTo(x0, yy + 1.5 * s.scale); ctx.lineTo(x0 + bw, yy + 1.5 * s.scale); ctx.stroke();
        ctx.shadowBlur = 0;
      });
      ctx.fillStyle = "#9bbcd8"; ctx.strokeStyle = "#141d27"; ctx.lineWidth = Math.max(1, 1.2 * s.scale);
      ctx.beginPath(); ctx.ellipse(s.x, y0 + 2 * s.scale, bw * 0.5, bh * 0.08, 0, 0, 7); ctx.fill(); ctx.stroke();
      ctx.strokeStyle = "#141d27"; ctx.lineWidth = 1; ctx.beginPath(); ctx.roundRect(x0, y0, bw, bh, 4 * s.scale); ctx.stroke();
      const num = b.reward.kind === "weapons" ? "W" : String(b.reward.amount);
      const iw = bw * 0.62, ih = bh * 0.24;
      ctx.fillStyle = "#0a1016"; ctx.strokeStyle = "#00e5ff"; ctx.lineWidth = Math.max(1, 1 * s.scale);
      ctx.beginPath(); ctx.roundRect(s.x - iw / 2, s.y - ih / 2, iw, ih, 3 * s.scale); ctx.fill(); ctx.stroke();
      ctx.fillStyle = "#5ff3ff"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
      ctx.font = `900 ${Math.round(13 * s.scale)}px 'Barlow Condensed', monospace`;
      ctx.fillText(num, s.x, s.y);
    } else {
      const bw = r * 1.5, bh = r * 2;
      const x0 = s.x - bw / 2, y0 = s.y - bh / 2, bulge = bw * 0.14;
      const grad = ctx.createLinearGradient(x0, 0, x0 + bw, 0);
      grad.addColorStop(0, "#3f2410"); grad.addColorStop(0.5, "#8a542a"); grad.addColorStop(1, "#3f2410");
      ctx.fillStyle = grad; ctx.strokeStyle = "rgba(0,0,0,0.35)"; ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x0, y0);
      ctx.quadraticCurveTo(x0 - bulge, s.y, x0, y0 + bh);
      ctx.lineTo(x0 + bw, y0 + bh);
      ctx.quadraticCurveTo(x0 + bw + bulge, s.y, x0 + bw, y0);
      ctx.closePath(); ctx.fill(); ctx.stroke();
      ctx.strokeStyle = "rgba(0,0,0,0.22)"; ctx.lineWidth = 1;
      for (let i = 1; i < 4; i++) { const vx = x0 + bw * (i / 4); ctx.beginPath(); ctx.moveTo(vx, y0 + 2 * s.scale); ctx.lineTo(vx, y0 + bh - 2 * s.scale); ctx.stroke(); }
      ctx.strokeStyle = "#d9b24e"; ctx.lineWidth = Math.max(1.5, 2.5 * s.scale);
      [y0 + bh * 0.16, s.y, y0 + bh * 0.84].forEach((hyv) => { ctx.beginPath(); ctx.moveTo(x0 - (hyv === s.y ? bulge * 0.7 : 0), hyv); ctx.lineTo(x0 + bw + (hyv === s.y ? bulge * 0.7 : 0), hyv); ctx.stroke(); });
      ctx.fillStyle = "#5a3418"; ctx.strokeStyle = "#d9b24e"; ctx.lineWidth = Math.max(1, 1.5 * s.scale);
      ctx.beginPath(); ctx.ellipse(s.x, y0 + 2 * s.scale, bw * 0.42, bh * 0.09, 0, 0, 7); ctx.fill(); ctx.stroke();
    }
    ctx.fillStyle = b.reward.kind === "weapons" ? "#f5c542" : "#37e0a0";
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.font = `800 ${Math.round(13 * s.scale)}px 'Barlow Condensed', sans-serif`;
    ctx.fillText(b.reward.kind === "weapons" ? "+WEAPONS" : `+${b.reward.amount} TROOPS`, s.x, s.y - r - 10 * s.scale);
  }

  _drawAlien(ctx, a) {
    const sp = a.sprite && this._img(a.sprite);
    if (sp) {
      const s = a.r * 2.7;
      ctx.save();
      ctx.shadowBlur = 8; ctx.shadowColor = a.elite ? "#9acb52" : "#8aff6a";
      ctx.drawImage(sp, a.x - s / 2, a.y - s / 2, s, s);
      ctx.restore();
      return;
    }
    ctx.save();
    ctx.translate(a.x, a.y);
    const col = a.elite ? "#7dff5a" : "#c04bff";
    ctx.shadowBlur = 10; ctx.shadowColor = col;
    ctx.fillStyle = a.elite ? "rgba(70,120,40,0.95)" : "rgba(90,40,120,0.95)";
    ctx.strokeStyle = col; ctx.lineWidth = 2;
    ctx.beginPath();
    const spikes = 7, R = a.r, r2 = a.r * 0.5;
    for (let i = 0; i < spikes * 2; i++) {
      const ang = (Math.PI / spikes) * i - Math.PI / 2;
      const rr = i % 2 === 0 ? R : r2;
      ctx.lineTo(Math.cos(ang) * rr, Math.sin(ang) * rr);
    }
    ctx.closePath(); ctx.fill(); ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.fillStyle = "#fff";
    ctx.beginPath(); ctx.arc(-a.r * 0.28, -a.r * 0.1, a.r * 0.16, 0, 7); ctx.arc(a.r * 0.28, -a.r * 0.1, a.r * 0.16, 0, 7); ctx.fill();
    ctx.restore();
  }

  _drawMedbay(ctx) {
    const w = this.w, h = this.h;
    const bw = w * 0.34, bh = bw * 0.98;
    const cx = w / 2, cy = h * 0.2;
    const sprite = this._img(this.rescueSprite);
    if (sprite) ctx.drawImage(sprite, cx - bw / 2, cy - bh / 2, bw, bh);
    else {
      ctx.fillStyle = "#3a2c1c"; ctx.strokeStyle = "#d9b24e"; ctx.lineWidth = 3;
      ctx.beginPath(); ctx.roundRect(cx - bw / 2, cy - bh / 2, bw, bh, 8); ctx.fill(); ctx.stroke();
      ctx.fillStyle = "#ff4560"; ctx.fillRect(cx - 12, cy - 30, 24, 8); ctx.fillRect(cx - 4, cy - 38, 8, 24);
    }
    // objective label + progress
    const rem = Math.max(0, this.rescueToKill - this.rescueKilled);
    ctx.fillStyle = "rgba(0,0,0,0.6)"; ctx.beginPath();
    ctx.roundRect(cx - w * 0.3, cy + bh / 2 + 6, w * 0.6, 20, 6); ctx.fill();
    ctx.fillStyle = "#ffd0b0"; ctx.font = "900 12px 'Barlow Condensed', monospace";
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillText(rem > 0 ? `CLEAR THE SWARM · ${rem} LEFT` : "BREACHING…", cx, cy + bh / 2 + 16);
    ctx.textAlign = "left"; ctx.textBaseline = "alphabetic";
  }

  _drawBoss(ctx) {
    const b = this.boss, w = this.w, h = this.h;
    b.bob = (b.bob || 0) + 0.04;
    const bw = w * 0.42, bh = h * 0.24;
    const cx = b.x, cy = b.y + Math.sin(b.bob) * 6;
    const sprite = this._img(this.bossSprite);
    ctx.save();
    if (b.flash > 0) { ctx.globalAlpha = 1; ctx.filter = "brightness(1.8)"; }
    if (sprite) ctx.drawImage(sprite, cx - bw / 2, cy - bh / 2, bw, bh);
    else {
      ctx.fillStyle = "rgba(120,40,150,0.95)"; ctx.strokeStyle = "#7dff5a"; ctx.lineWidth = 3;
      ctx.beginPath(); ctx.roundRect(cx - bw / 2, cy - bh / 2, bw, bh, 14); ctx.fill(); ctx.stroke();
    }
    ctx.restore();
    // HP bar
    const bx = w * 0.14, by = h * 0.055, bwid = w * 0.72, bhg = 12;
    ctx.fillStyle = "rgba(0,0,0,0.55)"; ctx.beginPath(); ctx.roundRect(bx - 2, by - 2, bwid + 4, bhg + 4, 6); ctx.fill();
    ctx.fillStyle = "#2a0d16"; ctx.beginPath(); ctx.roundRect(bx, by, bwid, bhg, 5); ctx.fill();
    ctx.fillStyle = "#ff3b4e"; ctx.beginPath(); ctx.roundRect(bx, by, bwid * (b.hp / b.max), bhg, 5); ctx.fill();
    ctx.fillStyle = "#fff"; ctx.font = "900 10px monospace"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillText(this.bossName, w / 2, by + bhg / 2);
    ctx.textAlign = "left"; ctx.textBaseline = "alphabetic";
  }

  // Combat vehicle — armoured pickup the squad rides behind; armour strip depletes
  // as it soaks hits, optional mounted turret on top.
  _drawVehicle(ctx, base) {
    const cx = base.x, cy = base.y + 22, bw = 64, bh = 30;
    const ratio = this.armorMax > 0 ? this.armor / this.armorMax : 0;
    ctx.save();
    this._groundShadow(ctx, cx, cy + bh * 0.45, bw * 0.5, 0.9);
    ctx.fillStyle = "#3a4250"; ctx.strokeStyle = "#141922"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(cx - bw / 2, cy - bh / 2, bw, bh, 6); ctx.fill(); ctx.stroke();
    ctx.fillStyle = "#2a313d"; ctx.beginPath(); ctx.roundRect(cx - bw / 2 + 6, cy - bh / 2 - 10, bw * 0.5, 14, 4); ctx.fill(); ctx.stroke();
    const ac = ratio > 0.6 ? "#37e0a0" : ratio > 0.3 ? "#f5c542" : ratio > 0 ? "#ff8a5c" : "#ff4560";
    ctx.fillStyle = "rgba(0,0,0,0.5)"; ctx.fillRect(cx - bw / 2 + 3, cy + bh / 2 - 7, bw - 6, 5);
    ctx.fillStyle = ac; ctx.fillRect(cx - bw / 2 + 3, cy + bh / 2 - 7, (bw - 6) * Math.max(0.03, ratio), 5);
    ctx.fillStyle = "#0c0f16";
    for (const wx of [cx - bw * 0.32, cx + bw * 0.32]) { ctx.beginPath(); ctx.arc(wx, cy + bh * 0.5, 6, 0, 7); ctx.fill(); }
    const turret = this.vehicle.turret;
    if (turret && turret !== "none") {
      const tc = turret === "missile" ? "#ff7a3c" : "#ffe08a";
      ctx.fillStyle = tc; ctx.strokeStyle = "#141922"; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.arc(cx, cy - bh * 0.2, 7, 0, 7); ctx.fill(); ctx.stroke();
      ctx.strokeStyle = tc; ctx.lineWidth = 3;
      ctx.beginPath(); ctx.moveTo(cx, cy - bh * 0.2); ctx.lineTo(cx, cy - bh * 0.2 - 16); ctx.stroke();
    }
    ctx.restore();
  }

  _drawBarricade(ctx, b, s) {
    const w = 42 * s.scale, h = 26 * s.scale;
    ctx.save();
    this._groundShadow(ctx, s.x, s.y + h * 0.4, w * 0.5, 0.8);
    ctx.fillStyle = "#5a4a30"; ctx.strokeStyle = "#c99a4a"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(s.x - w / 2, s.y - h / 2, w, h, 3); ctx.fill(); ctx.stroke();
    ctx.strokeStyle = "#f5c542"; ctx.lineWidth = 3;
    for (let i = -1; i <= 1; i++) { ctx.beginPath(); ctx.moveTo(s.x - w / 2 + i * 12, s.y + h / 2); ctx.lineTo(s.x - w / 2 + i * 12 + h, s.y - h / 2); ctx.stroke(); }
    ctx.restore();
  }

  // Campaign squad: named hero sprites side-by-side + trailing troops.
  _drawCampaignSquad(ctx) {
    const base = this._screen(0, this.laneNorm);
    if (this.vehicle) this._drawVehicle(ctx, base);
    const troop = this._img(this.sprites.troop);
    const shown = Math.min(this.troops, MAX_TROOPS);
    // trailing troops behind the heroes
    for (let i = 0; i < shown; i++) {
      const col = i % 6, row = Math.floor(i / 6);
      const tx = base.x + (col - 2.5) * 12;
      const ty = base.y + 34 + row * 10;
      if (troop) ctx.drawImage(troop, tx - 5, ty - 6, 10, 12);
      else { ctx.fillStyle = "rgba(0,213,255,0.55)"; ctx.beginPath(); ctx.arc(tx, ty, 3, 0, 7); ctx.fill(); }
    }
    const HERO_POS = { 1: [[0, 2]], 2: [[-18, 0], [18, 4]], 3: [[-30, 2], [0, 8], [30, 2]] };
    const HERO_COL = { jess: "#e2405a", nancy: "#3fd6d0", bazooka: "#8a9a52", blaze: "#ff7a3c" };
    const slots = HERO_POS[Math.min(3, this.roster.length)] || HERO_POS[3];
    const HW = 26, HH = 34;
    this.roster.forEach((id, i) => {
      const off = slots[i]; if (!off) return;
      const hx = base.x + off[0], hy = base.y + off[1];
      this._groundShadow(ctx, hx, hy + HH * 0.5, HW * 0.5, 0.9);
      const sp = this._img(this.heroSprites[id]);
      if (sp) ctx.drawImage(sp, hx - HW / 2, hy - HH / 2, HW, HH);
      else {
        ctx.fillStyle = HERO_COL[id] || "#e2405a";
        ctx.strokeStyle = "#fff"; ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.moveTo(hx, hy - 14); ctx.lineTo(hx - 9, hy + 10); ctx.lineTo(hx + 9, hy + 10); ctx.closePath(); ctx.fill(); ctx.stroke();
      }
    });
    // Nancy descent flare
    if (this.nancyDrop) {
      const nx = base.x + (this.roster.length >= 3 ? 30 : 24), ny = this.nancyDrop.y;
      const sp = this._img(this.heroSprites.nancy);
      ctx.save(); ctx.shadowBlur = 16; ctx.shadowColor = "#3fd6d0";
      if (sp) ctx.drawImage(sp, nx - HW / 2, ny - HH / 2, HW, HH);
      else { ctx.fillStyle = "#3fd6d0"; ctx.beginPath(); ctx.arc(nx, ny, 12, 0, 7); ctx.fill(); }
      ctx.restore();
    }
  }

  _drawSquad(ctx) {
    const base = this._screen(0, this.laneNorm);
    const hero = this._img(this.sprites.hero);
    const troop = this._img(this.sprites.troop);
    const shown = Math.min(this.troops, MAX_TROOPS);
    const heroPos = [[-13, 2], [13, 2], [-21, 20], [0, 20], [21, 20]];
    const pulse = 0.5 + 0.5 * Math.sin(performance.now() / 300);
    for (const [ox, oy] of heroPos) {
      const px = base.x + ox, py = base.y + oy + 9, rw = 13, rh = 6;
      ctx.save();
      ctx.shadowBlur = 12 + pulse * 8; ctx.shadowColor = "#39ff7a";
      ctx.strokeStyle = "rgba(57,255,122,0.9)"; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.ellipse(px, py, rw, rh, 0, 0, 7); ctx.stroke();
      ctx.shadowBlur = 0;
      ctx.fillStyle = `rgba(57,255,122,${(0.16 + pulse * 0.16).toFixed(3)})`;
      ctx.beginPath(); ctx.ellipse(px, py, rw * 0.8, rh * 0.8, 0, 0, 7); ctx.fill();
      ctx.fillStyle = "rgba(205,255,222,0.9)";
      ctx.beginPath(); ctx.ellipse(px, py, rw * 0.26, rh * 0.26, 0, 0, 7); ctx.fill();
      ctx.restore();
    }
    for (let i = 0; i < shown; i++) {
      const col = i % 6, row = Math.floor(i / 6);
      const edge = (col === 0 || col === 5) ? -row * 6 : 0;
      const tx = base.x + (col - 2.5) * 13;
      const ty = base.y + 30 + row * 11 + edge;
      if (troop) ctx.drawImage(troop, tx - 6, ty - 7, 12, 14);
      else { ctx.fillStyle = "rgba(0,213,255,0.55)"; ctx.beginPath(); ctx.arc(tx, ty, 3.5, 0, 7); ctx.fill(); }
    }
    for (const [ox, oy] of heroPos) {
      const hx = base.x + ox, hy = base.y + oy;
      if (hero) {
        if (this.facingCamera) { ctx.save(); ctx.translate(hx, hy); ctx.scale(1, -1); ctx.drawImage(hero, -11, -13, 22, 28); ctx.restore(); }
        else ctx.drawImage(hero, hx - 11, hy - 15, 22, 28);
      } else {
        ctx.fillStyle = "#00d5ff"; ctx.strokeStyle = "#eaffff"; ctx.lineWidth = 1.5;
        ctx.beginPath();
        if (this.facingCamera) { ctx.moveTo(hx, hy + 12); ctx.lineTo(hx - 8, hy - 8); ctx.lineTo(hx + 8, hy - 8); }
        else { ctx.moveTo(hx, hy - 12); ctx.lineTo(hx - 8, hy + 8); ctx.lineTo(hx + 8, hy + 8); }
        ctx.closePath(); ctx.fill(); ctx.stroke();
      }
    }
  }

  _autoSteer() {
    const s = this.strafe;
    if (s && (s.left || s.right)) {
      if (s.left && s.right) {
        const lDur = s.leftDur || 0.8, rDur = s.rightDur || 3.0;
        const t = ((performance.now() - this._t0) / 1000) % (lDur + rDur);
        this.targetLane = t < lDur ? -1 : 1;
      } else {
        this.targetLane = s.left ? -1 : 1;
      }
      return;
    }
    let best = null;
    for (const en of this.entities) {
      if (en.resolved || en.dz <= 6 || en.dz > 900) continue;
      if (en.type === "gate" && en.value >= 0) { if (!best || en.dz < best.dz) best = en; }
    }
    this.targetLane = Math.max(-1, Math.min(1, best ? best.laneNorm : 0));
  }

  _loop(now) {
    const dt = Math.min(50, now - this.last);
    this.last = now;
    this._update(dt);
    this._render();
    if (!this.over) this.raf = requestAnimationFrame(this._loop);
  }
}
